import {
  TIPOS_OBJETO_TABLETOP,
  IDS_PORTAS_TABLETOP,
  CORES_TRIAGEM,
  MATERIAIS_ABSORVENTES,
  CHAOS_TABLETOP,
  MARCACOES_CHECKLIST_ALTURA,
  EQUALIZACOES_ANCORAGEM,
  type CenaTabletop,
  type ObjetoTabletop,
} from './tipos'
import { copiarCenaTabletop } from './operacoes'
import { ehFolgasValidas } from './folgaCorda'
import { DESVIO_MAXIMO_CARGA_GRAUS } from './configuracaoAvisos'
import { IDS_FUNCAO_CORDA } from './funcoesCorda'
import { migrarCenaV1ParaV2, ehCenaV1 } from './migracao'

const tiposConhecidos = new Set<string>(TIPOS_OBJETO_TABLETOP)
const portasConhecidas = new Set<string>(IDS_PORTAS_TABLETOP)
const materiaisConhecidos = new Set<string>(MATERIAIS_ABSORVENTES)
const funcoesCordaConhecidas = new Set<string>(IDS_FUNCAO_CORDA)
const coresTriagemConhecidas = new Set<string>(CORES_TRIAGEM)

const ehRegistro = (valor: unknown): valor is Record<string, unknown> =>
  typeof valor === 'object' && valor !== null && !Array.isArray(valor)

const ehStringNaoVazia = (valor: unknown): valor is string =>
  typeof valor === 'string' && valor.trim().length > 0

const ehVetorFinito = (valor: unknown): valor is readonly [number, number, number] =>
  Array.isArray(valor) &&
  valor.length === 3 &&
  valor.every((coordenada) => typeof coordenada === 'number' && Number.isFinite(coordenada))

const ehEscalaPositiva = (valor: unknown): valor is readonly [number, number, number] =>
  ehVetorFinito(valor) && valor.every((componente) => componente > 0)

/** Só confere que o vínculo aponta para algo plausível; a existência do alvo é responsabilidade do chamador. */
const ehLocalVitimaValido = (valor: unknown): boolean => {
  if (!ehRegistro(valor)) return false
  if (valor.tipo === 'solo') return ehStringNaoVazia(valor.postura)
  if (valor.tipo === 'veiculo') return ehStringNaoVazia(valor.idVeiculo) && ehStringNaoVazia(valor.idAncora)
  return false
}

const ehParametrosValidos = (valor: unknown): boolean =>
  ehRegistro(valor) &&
  Object.values(valor).every((numero) => typeof numero === 'number' && Number.isFinite(numero))

const ehBlocoRiscoValido = (valor: unknown): boolean =>
  ehRegistro(valor) &&
  (valor.vazamentoAtivo === undefined || typeof valor.vazamentoAtivo === 'boolean') &&
  (valor.materiaisAceitos === undefined ||
    (Array.isArray(valor.materiaisAceitos) &&
      valor.materiaisAceitos.every((m) => typeof m === 'string' && materiaisConhecidos.has(m))))

const ehBlocoVitimaValido = (valor: unknown): boolean =>
  ehRegistro(valor) &&
  ehStringNaoVazia(valor.idPerfil) &&
  typeof valor.triagem === 'string' &&
  coresTriagemConhecidas.has(valor.triagem) &&
  typeof valor.colarCervical === 'boolean' &&
  ehLocalVitimaValido(valor.local)

const ehBlocoVeiculoValido = (valor: unknown): boolean => {
  if (!ehRegistro(valor)) return false
  if (!ehStringNaoVazia(valor.idPerfil) || !ehStringNaoVazia(valor.posicaoFinal)) return false
  if (!ehRegistro(valor.estadosPecas) || !ehRegistro(valor.vidros) || !ehRegistro(valor.protecoes)) return false
  if (typeof valor.insulfilm !== 'boolean') return false
  if (valor.deformacao !== undefined && valor.deformacao !== null) {
    if (!ehRegistro(valor.deformacao)) return false
    if (!ehStringNaoVazia(valor.deformacao.regiao) || !ehStringNaoVazia(valor.deformacao.nivel)) return false
    if (typeof valor.deformacao.intrusaoM !== 'number' || !Number.isFinite(valor.deformacao.intrusaoM)) return false
  }
  if (valor.deformacoes !== undefined) {
    if (!Array.isArray(valor.deformacoes)) return false
    for (const d of valor.deformacoes) {
      if (!ehRegistro(d) || !ehStringNaoVazia(d.regiao) || !ehStringNaoVazia(d.nivel)) return false
      if (typeof d.intrusaoM !== 'number' || !Number.isFinite(d.intrusaoM) || d.intrusaoM < 0) return false
      if (d.idAmassado !== undefined && (typeof d.idAmassado !== 'string' || !d.idAmassado.trim())) return false
      if (d.centroLongitudinalM !== undefined && (typeof d.centroLongitudinalM !== 'number' || !Number.isFinite(d.centroLongitudinalM))) return false
      if (d.idPorta !== undefined && (
        typeof d.idPorta !== 'string' ||
        !portasConhecidas.has(d.idPorta) ||
        d.regiao !== (d.idPorta.endsWith('Esquerda') ? 'lateralEsquerda' : 'lateralDireita')
      )) return false
      if (d.centroFaceM !== undefined && (typeof d.centroFaceM !== 'number' || !Number.isFinite(d.centroFaceM))) return false
      if (d.alturaContatoY !== undefined && (typeof d.alturaContatoY !== 'number' || !Number.isFinite(d.alturaContatoY))) return false
    }
  }
  if (valor.cortes !== undefined) {
    if (!Array.isArray(valor.cortes)) return false
    for (const c of valor.cortes) {
      if (!ehRegistro(c) || !ehStringNaoVazia(c.idPeca) || !ehStringNaoVazia(c.estado)) return false
    }
  }
  return true
}

const ehObjetoTabletop = (valor: unknown): valor is ObjetoTabletop => {
  if (!ehRegistro(valor) || !ehRegistro(valor.transformacao)) return false
  const base =
    ehStringNaoVazia(valor.id) &&
    ehStringNaoVazia(valor.nome) &&
    typeof valor.tipo === 'string' &&
    tiposConhecidos.has(valor.tipo) &&
    (valor.cor === undefined || typeof valor.cor === 'string') &&
    ehVetorFinito(valor.transformacao.posicao) &&
    ehVetorFinito(valor.transformacao.rotacao) &&
    ehEscalaPositiva(valor.transformacao.escala)
  if (!base) return false
  if (valor.vitima !== undefined && !ehBlocoVitimaValido(valor.vitima)) return false
  if (valor.veiculo !== undefined && !ehBlocoVeiculoValido(valor.veiculo)) return false
  if (valor.risco !== undefined && !ehBlocoRiscoValido(valor.risco)) return false
  if (valor.parametros !== undefined && !ehParametrosValidos(valor.parametros)) return false
  return true
}

/**
 * Vínculos consistentes entre os objetos da cena: toda vítima "no veículo"
 * aponta para um `idVeiculo` que existe, e no máximo uma vítima ocupa cada
 * âncora daquele veículo — mas nada impede várias vítimas no MESMO veículo
 * em âncoras diferentes.
 */
const vinculosConsistentes = (objetos: readonly ObjetoTabletop[]): boolean => {
  const idsVeiculo = new Set(objetos.filter((o) => o.veiculo).map((o) => o.id))
  const ocupacoes = new Set<string>()
  for (const objeto of objetos) {
    const local = objeto.vitima?.local
    if (!local || local.tipo !== 'veiculo') continue
    if (!idsVeiculo.has(local.idVeiculo)) return false
    const chave = `${local.idVeiculo}::${local.idAncora}`
    if (ocupacoes.has(chave)) return false
    ocupacoes.add(chave)
  }
  return true
}

/** A corda guarda referências a pontos; o objeto precisa existir, o ponto pode ter sumido (ponta solta). */
const ehCordaValida = (valor: unknown, idsObjetos: ReadonlySet<string>): boolean =>
  ehRegistro(valor) &&
  ehStringNaoVazia(valor.id) &&
  typeof valor.funcao === 'string' &&
  funcoesCordaConhecidas.has(valor.funcao) &&
  (valor.sentido === undefined || valor.sentido === 'paraFim' || valor.sentido === 'paraInicio') &&
  (valor.rotulo === undefined || typeof valor.rotulo === 'string') &&
  Array.isArray(valor.percurso) &&
  valor.percurso.length >= 2 &&
  (valor.folgas === undefined || ehFolgasValidas(valor.folgas, valor.percurso.length)) &&
  valor.percurso.every(
    (p) => ehRegistro(p) && ehStringNaoVazia(p.idObjeto) && ehStringNaoVazia(p.idPonto) && idsObjetos.has(p.idObjeto),
  )

const FIXACOES_POR_TIPO: Readonly<Record<string, number>> = { V: 2, W: 3, M: 4 }

const ehReferenciaValida = (p: unknown, idsObjetos: ReadonlySet<string>): boolean =>
  ehRegistro(p) && ehStringNaoVazia(p.idObjeto) && ehStringNaoVazia(p.idPonto) && idsObjetos.has(p.idObjeto)

const ehAncoragemValida = (valor: unknown, idsObjetos: ReadonlySet<string>): boolean =>
  ehRegistro(valor) &&
  ehStringNaoVazia(valor.id) &&
  typeof valor.tipo === 'string' &&
  valor.tipo in FIXACOES_POR_TIPO &&
  Array.isArray(valor.fixacoes) &&
  valor.fixacoes.length === FIXACOES_POR_TIPO[valor.tipo] &&
  valor.fixacoes.every((p) => ehReferenciaValida(p, idsObjetos)) &&
  ehReferenciaValida(valor.centro, idsObjetos) &&
  typeof valor.cargaKgf === 'number' &&
  Number.isFinite(valor.cargaKgf) &&
  valor.cargaKgf > 0 &&
  (valor.rotulo === undefined || typeof valor.rotulo === 'string') &&
  (valor.equalizacao === undefined || (EQUALIZACOES_ANCORAGEM as readonly unknown[]).includes(valor.equalizacao)) &&
  (valor.desvioCargaGraus === undefined ||
    (typeof valor.desvioCargaGraus === 'number' && Math.abs(valor.desvioCargaGraus) <= DESVIO_MAXIMO_CARGA_GRAUS))

const ehItemAcionamentoValido = (valor: unknown): boolean =>
  ehRegistro(valor) &&
  ehStringNaoVazia(valor.id) &&
  typeof valor.texto === 'string' &&
  typeof valor.confirmado === 'boolean' &&
  typeof valor.visivel === 'boolean'

const ehAcionamentoValido = (valor: unknown): boolean =>
  ehRegistro(valor) &&
  typeof valor.natureza === 'string' &&
  typeof valor.local === 'string' &&
  typeof valor.horario === 'string' &&
  typeof valor.recursos === 'string' &&
  Array.isArray(valor.informacoes) &&
  valor.informacoes.every(ehItemAcionamentoValido)

const ehChecklistAlturaValido = (valor: unknown): boolean =>
  ehRegistro(valor) && Object.values(valor).every((m) => (MARCACOES_CHECKLIST_ALTURA as readonly unknown[]).includes(m))

export function validarCenaTabletop(valor: unknown): valor is CenaTabletop {
  if (
    !ehRegistro(valor) ||
    valor.versao !== 2 ||
    typeof valor.nome !== 'string' ||
    (valor.historiaInicial !== undefined && typeof valor.historiaInicial !== 'string') ||
    !Array.isArray(valor.objetos) ||
    !valor.objetos.every(ehObjetoTabletop) ||
    (valor.acionamento !== undefined && !ehAcionamentoValido(valor.acionamento)) ||
    (valor.chao !== undefined && !(CHAOS_TABLETOP as readonly unknown[]).includes(valor.chao)) ||
    (valor.checklistAltura !== undefined && !ehChecklistAlturaValido(valor.checklistAltura))
  ) {
    return false
  }

  const ids = valor.objetos.map((objeto) => objeto.id)
  if (new Set(ids).size !== ids.length || !vinculosConsistentes(valor.objetos)) return false

  if (valor.cordas !== undefined) {
    if (!Array.isArray(valor.cordas)) return false
    const idsObjetos = new Set(ids)
    if (!valor.cordas.every((corda) => ehCordaValida(corda, idsObjetos))) return false
    const idsCordas = (valor.cordas as ReadonlyArray<{ id: string }>).map((corda) => corda.id)
    if (new Set(idsCordas).size !== idsCordas.length) return false
  }
  if (valor.ancoragens !== undefined) {
    if (!Array.isArray(valor.ancoragens)) return false
    const idsObjetos = new Set(ids)
    if (!valor.ancoragens.every((a) => ehAncoragemValida(a, idsObjetos))) return false
    const idsAncoragens = (valor.ancoragens as ReadonlyArray<{ id: string }>).map((a) => a.id)
    if (new Set(idsAncoragens).size !== idsAncoragens.length) return false
  }
  return true
}

export function serializarCenaTabletop(cena: CenaTabletop): string {
  if (!validarCenaTabletop(cena)) {
    throw new Error('Arquivo de table top inválido: a cena não pode ser serializada.')
  }
  return JSON.stringify(cena, null, 2)
}

/**
 * Aceita tanto o formato atual (v2) quanto arquivos exportados pelo antigo
 * Diorama 3D (v1, `versao: 1`), migrando-os automaticamente.
 */
export function desserializarCenaTabletop(json: string): CenaTabletop {
  let valor: unknown
  try {
    valor = JSON.parse(json) as unknown
  } catch {
    throw new Error('Arquivo de table top inválido: o conteúdo não é um JSON válido.')
  }

  if (ehCenaV1(valor)) {
    return migrarCenaV1ParaV2(valor)
  }

  if (!validarCenaTabletop(valor)) {
    throw new Error('Arquivo de table top inválido: estrutura ou valores não reconhecidos.')
  }

  return copiarCenaTabletop(valor)
}

